// useAuth — owns the "am I signed in?" state for the app shell.
//
// Strategy:
//   - On mount, call env.defaults to probe auth. If it 401s, set
//     status to "logged-out". If it succeeds, hold the userId / isAdmin.
//   - login() POSTs to /api/login. Two-step if the server returns
//     { requireTotp: true } — caller re-submits with totpCode set.
//   - logout() POSTs /api/logout and clears local state.
//   - listens for RPC_UNAUTHORIZED_EVENT and flips to logged-out so
//     a mid-session expiry surfaces as the login screen, not a stuck
//     interstitial.

import { useCallback, useEffect, useState } from "preact/hooks";
import { api, RPC_UNAUTHORIZED_EVENT } from "./rpc";

export type AuthStatus = "unknown" | "logged-in" | "logged-out";

export interface AuthInfo {
  userId: string;
  isAdmin: boolean;
  // Session scope from env.defaults. "kid-device" means this session is
  // the shared family-tablet account → the app shell renders the PIN
  // picker instead of the normal view. "kid" / "standard" otherwise.
  scope: "standard" | "kid" | "kid-device";
}

export interface LoginResult {
  ok: boolean;
  requireTotp?: boolean;
  error?: string;
}

export interface UseAuth {
  status: AuthStatus;
  info: AuthInfo | null;
  login: (id: string, password: string, totpCode?: string) => Promise<LoginResult>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

export function useAuth(): UseAuth {
  const [status, setStatus] = useState<AuthStatus>("unknown");
  const [info, setInfo] = useState<AuthInfo | null>(null);

  const refresh = useCallback(async () => {
    try {
      const env = await api.envDefaults();
      setInfo({ userId: env.userId, isAdmin: env.isAdmin, scope: env.scope || "standard" });
      setStatus("logged-in");
    } catch {
      setInfo(null);
      setStatus("logged-out");
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Mid-session 401 → flip back to the login screen.
  useEffect(() => {
    const onUnauth = () => { setInfo(null); setStatus("logged-out"); };
    window.addEventListener(RPC_UNAUTHORIZED_EVENT, onUnauth);
    return () => window.removeEventListener(RPC_UNAUTHORIZED_EVENT, onUnauth);
  }, []);

  const login = useCallback(async (id: string, password: string, totpCode?: string): Promise<LoginResult> => {
    try {
      const res = await fetch("/api/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ id, password, totpCode: totpCode || undefined }),
      });
      let body: any = {};
      try { body = await res.json(); } catch {}
      if (body.requireTotp) {
        return { ok: false, requireTotp: true, error: body.error };
      }
      if (!res.ok || !body.ok) {
        return { ok: false, error: body.error || `login failed (${res.status})` };
      }
      await refresh();
      return { ok: true };
    } catch (e: any) {
      return { ok: false, error: e?.message || "network failed" };
    }
  }, [refresh]);

  const logout = useCallback(async () => {
    try {
      await fetch("/api/logout", {
        method: "POST",
        credentials: "include",
        headers: { "X-Mirror-RPC": "1" },
      });
    } catch {}
    setInfo(null);
    setStatus("logged-out");
  }, []);

  return { status, info, login, logout, refresh };
}
